/**
 * Protected Route Component
 *
 * A guarda exige o que a ROTA declara — issue #408, causas C3, C4 e C5. Antes daqui ela
 * deduzia a exigência do catálogo do menu, e o menu só conhece tela de listagem, só com
 * permissão de leitura: o formulário de nova empresa abria para quem só via a lista, tela fora
 * do menu não tinha guarda nenhuma, e módulo fora do contrato tirava o item do menu sem fechar
 * o cadastro.
 *
 * São quatro perguntas, nesta ordem, e cada uma responde por uma parte:
 *
 * 1. o que a rota declara (`exige`) — rota calada é recusa, nunca passe-livre;
 * 2. o item de menu que cobre a rota, quando existe — é ele que carrega o corte por vínculo,
 *    que a lista de permissões do contexto não carrega;
 * 3. a permissão declarada, no manifesto do contexto ativo;
 * 4. a perspectiva declarada e o módulo contratado — módulo fora do contrato mantém a
 *    consulta e fecha a escrita (V5).
 */

import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

import { avaliar, type Exigencia } from '../authorization/pode';
import { useAuth } from '../stores/authStore';
import { useManifesto } from '../stores/manifestoStore';

import { ModuleAccessBanner } from './common/ModuleAccessBanner';

/** A tela de escolha de contexto (T3) é a única que roda com `precisaEscolher` de pé. */
export const CAMINHO_DA_ESCOLHA_DE_CONTEXTO = '/escolher-contexto';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * O que esta rota exige, montado com `pode()` ou `semPorta()`. É opcional no tipo só para
   * que a ausência seja RECUSADA em tempo de execução, e não um erro que só aparece na
   * compilação: quem cobra a declaração de cada rota é a regra de lint
   * `local/rota-declara-acesso`.
   */
  exige?: Exigencia;
}

const CENTRALIZADO = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  gap: 2,
} as const;

const Carregando: React.FC<{ mensagem: string }> = ({ mensagem }) => (
  <Box sx={CENTRALIZADO}>
    <CircularProgress size={48} thickness={4} />
    <Typography variant='body1' color='text.secondary'>
      {mensagem}
    </Typography>
  </Box>
);

/**
 * O código da permissão (`company.read`) é do suporte, não de quem lê a tela: fica no `title`,
 * que aparece ao pousar o mouse, e não no texto visível.
 */
const FRASE_DO_ACESSO_NEGADO = 'Esta tela não faz parte do seu acesso neste contexto.';

const AcessoNegado: React.FC<{ detalhe?: string; permissaoFaltante?: string }> = ({
  detalhe,
  permissaoFaltante,
}) => (
  <Box sx={{ ...CENTRALIZADO, p: 3, textAlign: 'center' }} title={permissaoFaltante}>
    <Typography variant='h5' color='error'>
      Acesso Negado
    </Typography>
    <Typography variant='body1' color='text.secondary'>
      {FRASE_DO_ACESSO_NEGADO}
    </Typography>
    {detalhe && detalhe !== FRASE_DO_ACESSO_NEGADO && (
      <Typography variant='body2' color='text.secondary'>
        {detalhe}
      </Typography>
    )}
  </Box>
);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, exige }) => {
  const location = useLocation();
  const { user, isAuthenticated, isLoading, hasHydrated } = useAuth();
  const { manifesto, escolhaPendente, erro, carregar } = useManifesto();

  const sessaoDePe = Boolean(isAuthenticated && user);

  useEffect(() => {
    if (sessaoDePe && !manifesto && !escolhaPendente) void carregar();
  }, [sessaoDePe, manifesto, escolhaPendente, carregar]);

  if (!hasHydrated || (isLoading && !user)) {
    return <Carregando mensagem='Verificando autenticação...' />;
  }

  if (!sessaoDePe) {
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  const naTelaDeEscolha = location.pathname === CAMINHO_DA_ESCOLHA_DE_CONTEXTO;

  // Quem ainda não tem contexto ativo é recusado com 403, e a recusa traz as opções — nenhuma,
  // quando não há vínculo. É a entrada normal da tela de escolha, não falha de carregamento:
  // tratá-la como erro punha "não conseguimos carregar o seu acesso" no lugar da escolha.
  if (!manifesto && escolhaPendente) {
    return naTelaDeEscolha ? (
      <>{children}</>
    ) : (
      <Navigate to={CAMINHO_DA_ESCOLHA_DE_CONTEXTO} replace />
    );
  }

  // Sem manifesto não há menu nem guarda, então a tela não abre. Falhando o pedido, o caminho
  // de saída é tentar de novo — o giro eterno da roda foi o que o estado de erro veio cortar.
  if (!manifesto && erro) {
    return (
      <Box sx={{ ...CENTRALIZADO, p: 3, textAlign: 'center' }}>
        <Typography variant='h6'>Não conseguimos carregar o seu acesso</Typography>
        <Typography variant='body2' color='text.secondary'>
          {erro}
        </Typography>
        <Button variant='contained' onClick={() => void carregar()}>
          Tentar de novo
        </Button>
      </Box>
    );
  }

  if (!manifesto) {
    return <Carregando mensagem='Carregando seu acesso...' />;
  }

  if (manifesto.precisaEscolher && !naTelaDeEscolha) {
    return <Navigate to={CAMINHO_DA_ESCOLHA_DE_CONTEXTO} replace />;
  }

  // As quatro perguntas moram em `avaliar`, e não aqui, porque os controles que levam a uma
  // tela precisam das mesmas respostas. Enquanto elas viviam dentro desta guarda, cada botão
  // reescrevia a que conseguia — a permissão — e "Aplicar Riscos" aparecia no contexto de uma
  // empresa só para dar em recusa.
  const veredito = avaliar(location.pathname, manifesto, exige);

  if (!veredito.liberado) {
    return <AcessoNegado detalhe={veredito.explicacao} permissaoFaltante={veredito.permissaoFaltante} />;
  }

  return (
    <>
      {veredito.moduloEncerrado && <ModuleAccessBanner modulo={veredito.moduloEncerrado} />}
      {children}
    </>
  );
};

export default ProtectedRoute;
